import { LinearProgress } from '@mui/material';
import { LicenseInfo } from '@mui/x-license';
import { useAutoGetCurrentUser } from '@pn/core/loaders/user/getCurrentUser';
import { useAutoTriggerAuth } from '@pn/core/operations/app';
import { useAppStorage, useCurrentUserStorage } from '@pn/core/storage';
import {
  AuthProvider,
  useAuthenticationService,
} from '@pn/services/authentication';
import { ErrorBoundaryProvider } from '@pn/services/loggers/AppSignalErrorBoundaryProvider';
import { StackedNotificationProvider } from '@pn/services/notifications/StackedNotificationProvider';
import { ReduxStoreProvider } from '@pn/services/redux/ReduxStoreProvider';
import { ErrorPage } from '@pn/ui/error-page';
import { PNThemeProvider } from '@pn/ui/theme/PNThemeProvider';
import { isNil } from 'lodash-es';
import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';
import { store } from 'src/application/storage/store';
import { useAutoInitializeApp } from 'src/application/useInitializeApp';
import { Effects } from 'src/ui/effects';
import { useServiceWorker } from 'src/ui/effects/useServiceWorker';
import { ReactContextsProvider } from './AppProviders';
import { Dashboards } from './Dashboards';
import { AuthRedirect } from './pages/AuthRedirect';

const EmailReport = React.lazy(() => import('./report/EmailReport'));

const About = React.lazy(() => import('./pages/About'));
const Account = React.lazy(() => import('./pages/Account'));
const Plans = React.lazy(() => import('./pages/Plans'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Terms = React.lazy(() => import('./pages/Terms'));
const OurData = React.lazy(() => import('./pages/our-data/OurData'));

if (!import.meta.env.VITE_APP_DATA_GRID_PRO_LICENSE_KEY) {
  throw new Error('VITE_APP_DATA_GRID_PRO_LICENSE_KEY not set');
}

LicenseInfo.setLicenseKey(import.meta.env.VITE_APP_DATA_GRID_PRO_LICENSE_KEY);

export function App() {
  return (
    <HelmetProvider>
      <ReduxStoreProvider store={store}>
        <PNThemeProvider defaultPNTheme="Stack Dark">
          <BrowserRouter>
            <AppWithRouter />
          </BrowserRouter>
        </PNThemeProvider>
      </ReduxStoreProvider>
    </HelmetProvider>
  );
}

function AppWithRouter() {
  const navigate = useNavigate();

  return (
    <AuthProvider navigate={navigate}>
      <ErrorBoundaryProvider errorPage={ErrorPage}>
        <StackedNotificationProvider>
          <AppRoutesGateway />
        </StackedNotificationProvider>
      </ErrorBoundaryProvider>
    </AuthProvider>
  );
}

function AppRoutesGateway() {
  const { isAppInitialized } = useAppStorage();

  useAutoInitializeApp(isAppInitialized);
  useServiceWorker();

  if (!isAppInitialized) {
    return <LinearProgress />;
  }

  return (
    <ReactContextsProvider>
      <AppRoutes />
    </ReactContextsProvider>
  );
}

function AppRoutes() {
  useAutoGetCurrentUser();
  const { user } = useCurrentUserStorage();
  const { isAuthenticating, isAuthenticated } = useAuthenticationService();
  const isAuthTriggered = useAutoTriggerAuth();

  const isAuthLoading =
    isAuthenticating || (isAuthenticated && isNil(user)) || !isAuthTriggered;

  if (isAuthLoading) {
    return <LinearProgress />;
  }

  return (
    <>
      <Effects />
      <React.Suspense fallback={<LinearProgress />}>
        <Routes>
          <Route path="/login" element={<AuthRedirect type="login" />} />
          <Route path="/register" element={<AuthRedirect type="signup" />} />
          <Route path="/signup" element={<AuthRedirect type="signup" />} />
          {/* special hidden page for email reports */}
          <Route path="/email-report" element={<EmailReport />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/our-data" element={<OurData />} />
          <Route path="/account" element={<Account />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/dashboards/*" element={<Dashboards />} />
          <Route path="*" element={<About />} />
        </Routes>
      </React.Suspense>
    </>
  );
}
